import { combineReducers } from "redux";
import {
  CartItemLegacy,
  OrderDispatchOption,
  CheckoutDeliveryAddress,
  TimeSlot,
  Price,
} from "~/types";
import { Action, InitAction } from "../action";
import { Ordering, OrderingFulfillmentTypeConfig } from "./OrderingService";

interface OrderingByLocation {
  [locationRef: string]: LocationOrderingState;
}

interface LocationOrderingState {
  cart: CartItemLegacy[];
  ordering: Ordering | null;
  isLoading: boolean;
  error: string | null;
  ref: string | null;
  serviceRef: string | null;
  deliveryAddress: CheckoutDeliveryAddress | null;
  notes: string | null;
  metadata: OrderingMetadata;
}

// Used for reordering metrics. Does not get sent to the backend.
export interface OrderingMetadata {
  isReorder: boolean;
  edited: boolean;
}

export type OrderingState = Readonly<{
  orderingByLocation: OrderingByLocation;
  serviceChargeRemoved: boolean;
  dispatchOption: OrderDispatchOption | null;
  dispatchTime: TimeSlot | null;
  voucherCode: string | null;
}>;

function orderingByLocation(state: OrderingByLocation = {}, action: Action) {
  switch (action.type) {
    case "INIT_ACTION":
      return initOrderDrafts(state, action);
    case "FETCH_ORDERING_CONFIG":
    case "FETCH_ORDERING_CONFIG_SUCCESS":
    case "FETCH_ORDERING_CONFIG_ERROR":
    case "ORDERING_SET_CART":
    case "ORDERING_ADD_ITEM":
    case "ORDERING_REMOVE_ITEM":
    case "ORDERING_UPDATE_ITEM":
    case "ORDERING_CLEAR_CART":
    case "ORDERING_SET_REF":
    case "ORDERING_SET_NOTES":
    case "ORDERING_SET_SERVICEREF":
    case "ORDERING_SET_DELIVERY_ADDRESS":
      return {
        ...state,
        [action.locationRef]: locationOrdering(
          state[action.locationRef],
          action,
        ),
      };
  }
  return state;
}

const locationOrdering = combineReducers<LocationOrderingState, Action>({
  cart,
  ordering,
  isLoading,
  error,
  ref,
  notes,
  serviceRef,
  deliveryAddress,
  metadata,
});

function metadata(
  state: OrderingMetadata = { isReorder: false, edited: false },
  action: Action,
) {
  switch (action.type) {
    // Ordering set cart is being used only for reordering
    case "ORDERING_SET_CART":
      return { ...state, isReorder: true };
    // clear cart is called when payment is received, reset state
    case "ORDERING_CLEAR_CART":
      return { ...state, isReorder: false, edited: false };
    case "ORDERING_ADD_ITEM":
    case "ORDERING_REMOVE_ITEM":
    case "ORDERING_UPDATE_ITEM":
      return { ...state, edited: true };
  }
  return state;
}

function initOrderDrafts(
  state: OrderingByLocation,
  action: InitAction,
): OrderingByLocation {
  return Object.keys(action.orderDrafts).reduce((acc, lid) => {
    const { cart, deliveryAddress, serviceRef, notes } =
      action.orderDrafts[lid];

    return {
      ...acc,
      [lid]: {
        ...locationOrdering(state[lid], action),
        cart,
        deliveryAddress,
        serviceRef,
        notes,
      },
    };
  }, state);
}

function error(state: string | null = null, action: Action) {
  switch (action.type) {
    case "FETCH_ORDERING_CONFIG_ERROR":
      return action.error;
    case "FETCH_ORDERING_CONFIG":
    case "FETCH_ORDERING_CONFIG_SUCCESS":
      return null;
  }
  return state;
}

function isLoading(state = false, action: Action) {
  switch (action.type) {
    case "FETCH_ORDERING_CONFIG":
      return true;

    case "FETCH_ORDERING_CONFIG_SUCCESS":
    case "FETCH_ORDERING_CONFIG_ERROR":
      return false;
  }
  return state;
}

function ref(state: string | null = null, action: Action) {
  switch (action.type) {
    case "ORDERING_SET_REF":
      return action.ref;
  }
  return state;
}

function notes(state: string | null = null, action: Action) {
  switch (action.type) {
    case "ORDERING_SET_CART":
      return action.cart.notes ?? null;
    case "ORDERING_SET_NOTES":
      return action.notes;
  }
  return state;
}

function deliveryAddress(
  state: CheckoutDeliveryAddress | null = null,
  action: Action,
) {
  switch (action.type) {
    case "ORDERING_SET_DELIVERY_ADDRESS":
      return action.deliveryAddress;
  }
  return state;
}

function serviceRef(state: string | null = null, action: Action) {
  switch (action.type) {
    case "ORDERING_SET_SERVICEREF":
      return action.serviceRef;
  }
  return state;
}

function voucherCode(state: string | null = null, action: Action) {
  switch (action.type) {
    case "SET_VOUCHERCODE":
      return action.voucherCode;
    case "INIT_ACTION":
      return action.voucherCode;
  }
  return state;
}

function ordering(state: Ordering | null = null, action: Action) {
  switch (action.type) {
    case "FETCH_ORDERING_CONFIG_SUCCESS":
      if (!action.ordering) return state;
      return action.ordering;
  }
  return state;
}

function cart(state: CartItemLegacy[] = [], action: Action) {
  switch (action.type) {
    case "ORDERING_SET_CART":
      return action.cart.items;

    case "ORDERING_ADD_ITEM":
      return [...state, action.item];

    case "ORDERING_UPDATE_ITEM":
      return state.map((item) => {
        if (item.id === action.item.id) {
          return action.item;
        }
        return item;
      });

    case "ORDERING_REMOVE_ITEM":
      return state.filter((item) => item.id !== action.cartItemId);

    case "ORDERING_CLEAR_CART":
      return [];
  }
  return state;
}

function dispatchOption(
  state: OrderDispatchOption | null = null,
  action: Action,
) {
  switch (action.type) {
    case "SET_ORDER_DISPATCH_OPTION":
      return action.dispatchOption;
    case "INIT_ACTION":
      return action.orderDispatchOption;
  }
  return state;
}

function serviceChargeRemoved(state = false, action: Action) {
  switch (action.type) {
    case "REMOVE_SERVICE_CHARGE":
      return true;
    case "APPLY_SERVICE_CHARGE":
      return false;
    case "ORDERING_CLEAR_CART":
      return false;
    case "SET_ORDER_DISPATCH_OPTION":
      return action.dispatchOption !== "SERVICE";
    case "INIT_ACTION":
      return action.serviceChargeRemoved;
  }

  return state;
}

function dispatchTime(state: TimeSlot | null = null, action: Action) {
  switch (action.type) {
    case "SET_DISPATCH_TIME":
      return action.timeSlot;
    case "INIT_ACTION":
      return action.timeSlot;
  }
  return state;
}

export const reducer = combineReducers<OrderingState, Action>({
  orderingByLocation,
  serviceChargeRemoved,
  dispatchOption,
  dispatchTime,
  voucherCode,
});

// Selectors

export function getLocationOrdering(
  state: OrderingState,
  locationRef: string,
): LocationOrderingState | null {
  return state.orderingByLocation[locationRef]
    ? state.orderingByLocation[locationRef]
    : null;
}

export function getOrderingMetadata(
  state: OrderingState,
  locationRef: string,
): OrderingMetadata | null {
  return getLocationOrdering(state, locationRef)?.metadata ?? null;
}

export function getRef(
  state: OrderingState,
  locationRef: string,
): string | null {
  return getLocationOrdering(state, locationRef)?.ref ?? null;
}

export function getNotes(state: OrderingState, locationRef: string): string {
  return getLocationOrdering(state, locationRef)?.notes ?? "";
}

export function getServiceRef(
  state: OrderingState,
  locationRef: string,
): string {
  return getLocationOrdering(state, locationRef)?.serviceRef ?? "";
}

export function getDeliveryAddress(
  state: OrderingState,
  locationRef: string,
): CheckoutDeliveryAddress | null {
  return getLocationOrdering(state, locationRef)?.deliveryAddress ?? null;
}

export function getIsLoading(
  state: OrderingState,
  locationRef: string,
): boolean {
  return getLocationOrdering(state, locationRef)?.isLoading ?? false;
}

export function getError(
  state: OrderingState,
  locationRef: string,
): string | null {
  return getLocationOrdering(state, locationRef)?.error ?? null;
}

export function getCart(
  state: OrderingState,
  locationRef: string,
): CartItemLegacy[] {
  return getLocationOrdering(state, locationRef)?.cart ?? [];
}

export function getVoucherCode(state: OrderingState): string {
  return state.voucherCode ?? "";
}

export function getCartItem(
  state: OrderingState,
  locationRef: string,
  cartItemId: string,
): CartItemLegacy | undefined {
  return getCart(state, locationRef).find((i) => i.id === cartItemId);
}

export function getPickupWaitTime(
  state: OrderingState,
  locationRef: string,
): string | null {
  const minutes =
    getLocationOrdering(state, locationRef)?.ordering?.pickupConfig?.waitTime ??
    null;
  if (!minutes) return null;
  return `${minutes} minute${minutes === 1 ? "" : "s"}`;
}

export function getServiceWaitTime(
  state: OrderingState,
  locationRef: string,
): string | null {
  const minutes =
    getLocationOrdering(state, locationRef)?.ordering?.serviceConfig
      ?.waitTime ?? null;
  if (!minutes) return null;
  return `${minutes} minute${minutes === 1 ? "" : "s"}`;
}

export function getDeliveryTime(
  state: OrderingState,
  locationRef: string,
): string | null {
  const deliveryConfig = getLocationOrdering(state, locationRef)?.ordering
    ?.deliveryConfig;

  if (!deliveryConfig) {
    return null;
  }

  if (deliveryConfig.minutesMin === 0 && deliveryConfig.minutesMax === 0) {
    return null;
  }

  return `${deliveryConfig.minutesMin} to ${deliveryConfig.minutesMax} minutes`;
}

export function getDeliveryFee(
  state: OrderingState,
  locationRef: string,
): number | null {
  return (
    getLocationOrdering(state, locationRef)?.ordering?.deliveryConfig
      ?.feeAmount ?? null
  );
}

export function getDeliveryPrice(
  state: OrderingState,
  locationRef: string,
): Price | null {
  return (
    getLocationOrdering(state, locationRef)?.ordering?.deliveryConfig
      ?.feePrice ?? null
  );
}

export function getMinimumAmount(
  state: OrderingState,
  locationRef: string,
): number | null {
  return (
    getLocationOrdering(state, locationRef)?.ordering?.deliveryConfig
      ?.minimumAmount ?? null
  );
}

export function getOrderDispatchOption(
  state: OrderingState,
  locationRef: string,
): OrderDispatchOption | null {
  const available = getAvailableOrderDispatchOptions(state, locationRef);

  if (!available.some((a) => a === state.dispatchOption)) {
    return available[0] || null;
  }

  return state.dispatchOption;
}

export function getOrderServiceChargeRemoved(state: OrderingState): boolean {
  return state.serviceChargeRemoved || false;
}

export function getAvailableOrderDispatchOptions(
  state: OrderingState,
  locationRef: string,
): OrderDispatchOption[] {
  const ordering = getLocationOrdering(state, locationRef)?.ordering;
  const options: OrderDispatchOption[] = [];

  // In priority order, if customer hasn't got one selected
  // will pick the first, see getOrderDispatchOption
  if (ordering?.pickupConfig) {
    options.push("PICK_UP");
  }
  if (ordering?.deliveryConfig) {
    options.push("DELIVERY");
  }
  if (ordering?.serviceConfig) {
    options.push("SERVICE");
  }

  return options;
}

export function getDeliveryActive(
  state: OrderingState,
  locationRef: string,
): boolean | undefined {
  const deliveryConfig = getLocationOrdering(state, locationRef)?.ordering
    ?.deliveryConfig;

  return (
    deliveryConfig?.active ||
    selectedDispatchTimeSlotIsAvailable(
      state.dispatchTime,
      deliveryConfig?.timeSlots || [],
    )
  );
}

export function getPickupActive(
  state: OrderingState,
  locationRef: string,
): boolean | undefined {
  const pickupConfig = getLocationOrdering(state, locationRef)?.ordering
    ?.pickupConfig;

  return (
    pickupConfig?.active ||
    selectedDispatchTimeSlotIsAvailable(
      state.dispatchTime,
      pickupConfig?.timeSlots || [],
    )
  );
}

export function getServiceActive(
  state: OrderingState,
  locationRef: string,
): boolean | undefined {
  return getLocationOrdering(state, locationRef)?.ordering?.serviceConfig
    ?.active;
}

function selectedDispatchTimeSlotIsAvailable(
  selectedTimeSlot: TimeSlot | null,
  availableTimeSlots: TimeSlot[],
) {
  if (!selectedTimeSlot) return false;

  const foundSlot = availableTimeSlots.find(
    (timeSlot) => timeSlot.ref === selectedTimeSlot.ref,
  );

  return Boolean(foundSlot?.available);
}

export function getServiceValidRefs(
  state: OrderingState,
  locationRef: string,
): { ref: string }[] | null | undefined {
  return getLocationOrdering(state, locationRef)?.ordering?.serviceConfig
    ?.validRefs;
}

export function getServiceChargeLabel(
  state: OrderingState,
  locationRef: string,
): string {
  return (
    getLocationOrdering(state, locationRef)?.ordering?.serviceConfig
      ?.serviceChargeLabel || "Service Charge"
  );
}

export function getServiceChargeRate(
  state: OrderingState,
  locationRef: string,
): number {
  return (
    getLocationOrdering(state, locationRef)?.ordering?.serviceConfig
      ?.serviceChargeRate || 0
  );
}

export function getOrderDispatchOptionConfig(
  state: OrderingState,
  locationRef: string,
): OrderingFulfillmentTypeConfig | undefined {
  const orderDispatchOption = getOrderDispatchOption(state, locationRef);
  switch (orderDispatchOption) {
    case "DELIVERY":
      return (
        getLocationOrdering(state, locationRef)?.ordering?.deliveryConfig ??
        undefined
      );
    case "PICK_UP":
      return (
        getLocationOrdering(state, locationRef)?.ordering?.pickupConfig ??
        undefined
      );
    case "SERVICE":
      return (
        getLocationOrdering(state, locationRef)?.ordering?.serviceConfig ??
        undefined
      );

    default:
      return undefined;
  }
}

export function allowOrderNotes(
  state: OrderingState,
  locationRef: string,
): boolean | undefined {
  return getOrderDispatchOptionConfig(state, locationRef)?.allowOrderNotes;
}

export function getOrderNotesMaxLength(
  state: OrderingState,
  locationRef: string,
): number | undefined {
  return (
    getOrderDispatchOptionConfig(state, locationRef)?.orderNotesMaxLength ??
    undefined
  );
}

export function allowItemNotes(
  state: OrderingState,
  locationRef: string,
): boolean | undefined {
  return getOrderDispatchOptionConfig(state, locationRef)?.allowItemNotes;
}

export function getItemNotesMaxLength(
  state: OrderingState,
  locationRef: string,
): number | undefined {
  return (
    getOrderDispatchOptionConfig(state, locationRef)?.itemNotesMaxLength ??
    undefined
  );
}

export function getTipsEnabledDelivery(
  state: OrderingState,
  locationRef: string,
): boolean {
  return (
    getLocationOrdering(state, locationRef)?.ordering?.deliveryConfig
      ?.tipsEnabled || false
  );
}

export function getTipsEnabledPickup(
  state: OrderingState,
  locationRef: string,
): boolean {
  return (
    getLocationOrdering(state, locationRef)?.ordering?.pickupConfig
      ?.tipsEnabled || false
  );
}

export function getTipsEnabledService(
  state: OrderingState,
  locationRef: string,
): boolean {
  return (
    getLocationOrdering(state, locationRef)?.ordering?.serviceConfig
      ?.tipsEnabled || false
  );
}

export function getTipRatiosDelivery(
  state: OrderingState,
  locationRef: string,
): number[] {
  return (
    getLocationOrdering(state, locationRef)?.ordering?.deliveryConfig
      ?.tipRatios || []
  );
}

export function getTipRatiosPickup(
  state: OrderingState,
  locationRef: string,
): number[] {
  return (
    getLocationOrdering(state, locationRef)?.ordering?.pickupConfig
      ?.tipRatios || []
  );
}

export function getTipRatiosService(
  state: OrderingState,
  locationRef: string,
): number[] {
  return (
    getLocationOrdering(state, locationRef)?.ordering?.serviceConfig
      ?.tipRatios || []
  );
}

export function getExternalPaymentEnabledDelivery(
  state: OrderingState,
  locationRef: string,
): boolean {
  return (
    getLocationOrdering(state, locationRef)?.ordering?.deliveryConfig
      ?.externalPaymentEnabled || false
  );
}

export function getExternalPaymentEnabledPickup(
  state: OrderingState,
  locationRef: string,
): boolean {
  return (
    getLocationOrdering(state, locationRef)?.ordering?.pickupConfig
      ?.externalPaymentEnabled || false
  );
}

export function getExternalPaymentEnabledService(
  state: OrderingState,
  locationRef: string,
): boolean {
  return (
    getLocationOrdering(state, locationRef)?.ordering?.serviceConfig
      ?.externalPaymentEnabled || false
  );
}

export function getTimeSlotsPickup(
  state: OrderingState,
  locationRef: string,
): TimeSlot[] {
  return (
    getLocationOrdering(state, locationRef)?.ordering?.pickupConfig
      ?.timeSlots || []
  );
}

export function getTimeSlotsDelivery(
  state: OrderingState,
  locationRef: string,
): TimeSlot[] {
  return (
    getLocationOrdering(state, locationRef)?.ordering?.deliveryConfig
      ?.timeSlots || []
  );
}
